import merge from "lodash/merge";
import { useEffect, useMemo, useState } from "react";
import { DEFAULT_HIGHCHARTS_3D_PIE_CHART_OPTIONS } from "@constants/highcharts";
import { WorkflowReportFraudResponse } from "@idw-types/data/reports";
import { Assertion, RiskLevel } from "@idw-types/data/reference-data/generic";
import {
    HIGH_FRAUD_COLOR,
    LOW_FRAUD_COLOR,
    MEDIUM_FRAUD_COLOR,
    MEDIUM_LOW_FRAUD_COLOR,
    VERY_HIGH_FRAUD_COLOR
} from "@constants/colors";
import { Highcharts3dPieChartContainer } from "../../../../common/highcharts-3d-pie-chart-container/highcharts-3d-pie-chart-container";
import { RiskLevelTypes } from "@enums/risk-level-types";
import { resources } from "@constants/hardcoded-strings";

type WorkflowReportFraud3DPieChartProps = {
    fraudResponse: WorkflowReportFraudResponse;
    assertions: Array<Assertion>;
    riskLevels: Array<RiskLevel>;
};


export const WorkflowReportFraud3DPieChart = (props: WorkflowReportFraud3DPieChartProps) => {
    const { fraudResponse, assertions, riskLevels } = props;

    const getColorForRiskLevel = (riskLevel: string) => {
        switch(riskLevel) {
            case RiskLevelTypes.HIGH:
                return VERY_HIGH_FRAUD_COLOR;
              case RiskLevelTypes.MEDIUM_HIGH:
                return HIGH_FRAUD_COLOR;
              case RiskLevelTypes.MEDIUM:
                return MEDIUM_FRAUD_COLOR;
              case RiskLevelTypes.MEDIUM_LOW:
                return MEDIUM_LOW_FRAUD_COLOR;
              case RiskLevelTypes.LOW:
                return LOW_FRAUD_COLOR;
            default:
                return LOW_FRAUD_COLOR;
        }
    };

    const data = useMemo(() => {
        const dataToDisplay: Array<{ name: string; description: string; color: string; y: number; }> = fraudResponse.map(fraudDataObject => {
            const assertion = assertions.find(assertion => `${assertion.id}` === `${fraudDataObject.assertionID}`);
            const riskLevel = riskLevels.find(riskLevel => `${riskLevel.id}` === `${fraudDataObject.riskLevel}`);

            return {
                name: assertion?.alternateName || assertion?.shortName || `${fraudDataObject.assertionID}`,
                description: assertion?.description || resources.common.notAvailable,
                riskLevel: riskLevel?.label || resources.common.notAvailable,
                color: getColorForRiskLevel(riskLevel?.label),
                y: fraudDataObject.count
            };
        });

        return dataToDisplay;
    }, [fraudResponse]);

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>(merge({}, DEFAULT_HIGHCHARTS_3D_PIE_CHART_OPTIONS, {
        chart: {
            height: "400"
        },
        series: [{
            data: data
        }],
        plotOptions: {
            pie: {
                dataLabels: {
                    format: "{point.riskLevel} - {point.name}, {point.y}"
                }
            }
        },
        tooltip: {
            headerFormat: "",
            pointFormat: "<span><b>Name:</b> {point.name}</b><br /><b>Description:</b> {point.description}<br /><b>Risk Level:</b> {point.riskLevel}<br/><b>Count:</b> {point.y}</span",
            useHTML: true
        }
    }));

    useEffect(() => {
        const newChartOptions: Highcharts.Options = merge({}, chartOptions, {
            series: [{
                data: data
            }]
        });

        setChartOptions(newChartOptions);

    }, [data]);

    return <Highcharts3dPieChartContainer options={chartOptions} />;
};